import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authAPI } from "../services/api";
import "./Onboarding.css";

const onboardingSteps = [
  {
    id: "msp_account",
    title: "Create MSP account",
    description: "Set up your Managed Service Provider workspace to centralize client environments.",
    link: "/settings",
    details: [
      "Confirm your organization profile and billing contact.",
      "Invite additional MSP administrators and analysts.",
      "Define default alerting preferences.",
    ],
  },
  {
    id: "add_tenant",
    title: "Add tenant",
    description: "Register the tenant or customer you are onboarding for external discovery.",
    link: "/msp",
    details: [
      "Capture the tenant name, primary contact, and location.",
      "Assign ownership and escalation paths.",
      "Tag the tenant with a service tier.",
    ],
  },
  {
    id: "input_domain",
    title: "Input domain",
    description: "Provide the primary domain you want to discover and monitor.",
    link: "/msp",
    details: [
      "Enter the base domain (e.g., tenant-example.com).",
      "Add optional domain aliases used by the tenant.",
      "Confirm the discovery scope before verification.",
    ],
  },
  {
    id: "verify_ownership",
    title: "Verify ownership",
    description: "Validate that the tenant authorizes external discovery.",
    link: "/msp",
    details: [
      "Upload a DNS TXT record or HTTP verification file.",
      "Confirm email-based verification with the primary contact.",
      "Review the verification audit log.",
    ],
  },
  {
    id: "schedule_scan",
    title: "Schedule first scan",
    description: "Launch the inaugural discovery scan and configure cadence.",
    link: "/scans",
    details: [
      "Choose an immediate or scheduled start time.",
      "Enable notifications for new external assets.",
      "Review scan configuration and confirm.",
    ],
  },
];

function Onboarding() {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const totalSteps = onboardingSteps.length;
  const step = onboardingSteps[currentStep];

  useEffect(() => {
    const fetchState = async () => {
      try {
        const response = await authAPI.getOnboardingState();
        if (response.data) {
          setCompletedSteps(response.data);
        }
      } catch (err) {
        console.error("Failed to load onboarding state", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchState();
  }, []);

  const progressPercent = useMemo(() => {
    if (totalSteps === 0) return 0;
    const completedCount = Object.values(completedSteps).filter(Boolean).length;
    return Math.round((completedCount / totalSteps) * 100);
  }, [completedSteps, totalSteps]);

  const toggleStepCompletion = async (stepId) => {
    const newState = {
      ...completedSteps,
      [stepId]: !completedSteps[stepId]
    };
    setCompletedSteps(newState);
    
    try {
      await authAPI.updateOnboardingState(newState);
    } catch (err) {
      console.error("Failed to update onboarding state", err);
      // Optional: Revert state on failure
    }
  };

  const goNext = () => {
    if (!completedSteps[step.id]) {
      toggleStepCompletion(step.id);
    }
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  const goBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  if (isLoading) {
    return <div className="onboarding-page loading">Loading...</div>;
  }

  return (
    <div className="onboarding-page">
      <div className="card">
        <div className="onboarding-header">
          <div>
            <h1 className="onboarding-title">External Discovery Onboarding</h1>
            <p className="onboarding-subtitle">
              Complete the checklist to set up external asset discovery for a new tenant.
            </p>
          </div>
          <div className="onboarding-progress">
            <span>{Object.values(completedSteps).filter(Boolean).length} of {totalSteps} Tasks Completed</span>
            <div className="onboarding-progress-bar">
              <div className="onboarding-progress-fill" style={{ width: `${progressPercent}%` }} />
            </div>
          </div>
        </div>

        <div className="onboarding-body">
          <aside className="onboarding-steps">
            {onboardingSteps.map((item, index) => (
              <button
                key={item.title}
                type="button"
                className={`onboarding-step ${index === currentStep ? "active" : ""} ${completedSteps[item.id] ? "completed" : ""}`}
                onClick={() => setCurrentStep(index)}
              >
                <span className="onboarding-step-index">
                  {completedSteps[item.id] ? "✓" : index + 1}
                </span>
                <span className="onboarding-step-title">{item.title}</span>
              </button>
            ))}
          </aside>

          <section className="onboarding-content">
            <div className="step-header">
              <h2>{step.title}</h2>
              <div className="step-actions">
                {step.link && (
                  <button 
                    className="btn btn-outline-cyan btn-sm"
                    onClick={() => navigate(`${step.link}?returnTo=/onboarding`)}
                    style={{ marginRight: "15px" }}
                  >
                    Go to Page
                  </button>
                )}
                <label className="checkbox-container">
                  <input 
                    type="checkbox" 
                    checked={!!completedSteps[step.id]} 
                    onChange={() => toggleStepCompletion(step.id)} 
                  />
                  <span className="checkmark"></span>
                  Complete Task
                </label>
              </div>
            </div>
            <p className="onboarding-description">{step.description}</p>
            <ul className="onboarding-details">
              {step.details.map((detail) => (
                <li key={detail}>{detail}</li>
              ))}
            </ul>
          </section>
        </div>

        <div className="onboarding-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={goBack}
            disabled={currentStep === 0}
          >
            Back
          </button>
          {currentStep < totalSteps - 1 ? (
            <button type="button" className="btn btn-primary" onClick={goNext}>
              Next Step
            </button>
          ) : (
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={() => navigate("/external-assets")}
              disabled={Object.values(completedSteps).filter(Boolean).length < totalSteps}
            >
              Finish Onboarding
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
